// Centralised content + assets for Great Outdoors Landscaping
// All imagery sourced from real client projects.

const ASSETS = "https://customer-assets.emergentagent.com";

// Real project photos provided by the owner
export const PHOTOS = {
    // New hero-quality showcase shots
    lushFloweringGarden: `${ASSETS}/job_outdoor-vision-demo/artifacts/jp2c4z0v_pexels-photo-25972319.jpeg`,
    poolPatioOutdoorLiving: `${ASSETS}/job_outdoor-vision-demo/artifacts/jny0xwqk_pexels-photo-12700534.jpeg`,
    walkwayWithConeflowers: `${ASSETS}/job_outdoor-vision-demo/artifacts/60zo8p29_da8c02dc4f768e4f620e63bd6885da3b.jpeg`,
    retainingWallCurved: `${ASSETS}/job_outdoor-vision-demo/artifacts/edstpyd2_45f0e1962038b43507a9bac50e677110.jpeg`,
    dryCreekBed: `${ASSETS}/job_outdoor-vision-demo/artifacts/a7d4zg5x_7a5294f0245745b4c650e8a915b01064.jpeg`,

    // Existing photos
    frontYardArborvitae: `${ASSETS}/job_outdoor-vision-demo/artifacts/98oi0yqn_IMG_0081.jpeg`,
    circularStonePatio: `${ASSETS}/job_outdoor-vision-demo/artifacts/qjln92bb_4675190347214807692%2B2-b8ec66ef.jpeg`,
    outdoorKitchen: `${ASSETS}/job_outdoor-vision-demo/artifacts/lczt96il_IMG_0022.jpeg`,
    cobblestoneDriveway: `${ASSETS}/job_outdoor-vision-demo/artifacts/cftopywu_7F6CCF0B-3165-4908-B046-D133B7F5C2B6.jpeg`,
    bluestonePatioDusk: `${ASSETS}/job_outdoor-vision-demo/artifacts/4au4fis7_9152678841977249657%2B2.jpeg`,
    lawnPaverEdge: `${ASSETS}/job_outdoor-vision-demo/artifacts/qapb4ktn_pexels-photo-33969451.jpeg`,
    stoneWallHedge: `${ASSETS}/job_outdoor-vision-demo/artifacts/ruhc3g2z_IMG_0007%2B2-61eada4e.jpeg`,
    project8: `${ASSETS}/job_outdoor-vision-demo/artifacts/fzt6fnq3_DDF638E0-6F5D-4C4B-B971-759E0A621AB8.jpeg`,

    // Commercial-specific
    commercialManicuredGarden: `${ASSETS}/job_outdoor-vision-demo/artifacts/azdr1ps7_pexels-photo-33752159.jpeg`,
    commercialAerialGreenSpace: `${ASSETS}/job_outdoor-vision-demo/artifacts/s6c6pw59_pexels-photo-14098841.jpeg`,
    snowPlowTruck: `${ASSETS}/job_outdoor-vision-demo/artifacts/fzzbvo77_pexels-photo-35826492.jpeg`,
    drainageGrate: `${ASSETS}/job_outdoor-vision-demo/artifacts/m146wlny_pexels-photo-10419020.jpeg`,
    gardenWithDryCreek: `${ASSETS}/job_outdoor-vision-demo/artifacts/umb9d356_7a5294f0245745b4c650e8a915b01064.jpeg`,
};

export const BRAND = {
    name: "Great Outdoors Landscaping",
    short: "Great Outdoors",
    tagline: "Landscape & Hardscape Specialists",
    established: 1998,
    address: "313 Park Avenue G2, Falls Church, VA 22046",
    officePhone: "(703) 942-6488",
    cellPhone: "(703) 675-3364",
    email: "matt@greatoutdoorslandscaping.net",
    serviceArea: "Arlington, Falls Church & the Greater DMV Area",
    instagram: "https://www.instagram.com/greatoutdoorsolutions/",
    facebook: "https://www.facebook.com/greatoutdoorslandscapings",
    logoLight: "https://customer-assets.emergentagent.com/job_c1ccbcd4-d459-4758-88e6-1a0e68662220/artifacts/84lvscgz_4-88b4afc7-f301f81c.png",
    logoDark: "https://customer-assets.emergentagent.com/job_outdoor-vision-demo/artifacts/fx9iler5_3.png",
};

export const NAV_LINKS = [
    { label: "Home", to: "/" },
    { label: "About", to: "/about" },
    { label: "Services", to: "/services" },
    { label: "Process", to: "/process" },
    { label: "Gallery", to: "/gallery" },
    { label: "Maintenance", to: "/maintenance" },
    { label: "Contact", to: "/contact" },
];

export const HERO_SLIDES = [
    {
        url: PHOTOS.lushFloweringGarden,
        eyebrow: "Established 1998",
        title: "Bringing Your Outdoor\nVision to Life",
        sub: "Premium landscape design, garden installation, and hardscape craftsmanship across Arlington, Falls Church, and the DMV.",
    },
    {
        url: PHOTOS.poolPatioOutdoorLiving,
        eyebrow: "Outdoor Living",
        title: "The Backyard Where\nYour Family Gathers",
        sub: "Pool surrounds, fire pits, and patios designed for the way you actually use the yard.",
    },
    {
        url: PHOTOS.bluestonePatioDusk,
        eyebrow: "Design • Build • Maintain",
        title: "Hardscapes That\nElevate the Everyday",
        sub: "Custom patios, walkways, and retaining walls — built to last, lit to live in.",
    },
    {
        url: PHOTOS.outdoorKitchen,
        eyebrow: "Year-Round Care",
        title: "Quietly Crafted\nFor Over 25 Years",
        sub: "A reputation built one property, one season, one detail at a time.",
    },
];

// Owner-supplied copy
export const SERVICES_RESIDENTIAL = [
    {
        title: "Landscape Design & Installation",
        desc: "Custom outdoor spaces designed and built to match your vision, property, and lifestyle.",
        image: PHOTOS.frontYardArborvitae,
        bullets: ["Custom property plans", "Soil prep, grading & edging", "Multi-season planting palettes"],
        icon: "Plant",
    },
    {
        title: "Garden & Plant Installation",
        desc: "Thoughtfully selected plants, flowers, and greenery to bring color and life to your landscape.",
        image: PHOTOS.gardenWithDryCreek,
        bullets: ["Native & ornamental species", "Bed shaping & fresh mulch", "Pollinator-friendly plant palettes"],
        icon: "Flower",
    },
    {
        title: "Hardscaping Design & Installation",
        desc: "Patios, walkways, retaining walls, and stone features that add structure and lasting beauty.",
        image: PHOTOS.bluestonePatioDusk,
        bullets: ["Flagstone & paver patios", "Cobblestone driveways & walkways", "Retaining walls, steps & seat walls"],
        icon: "Wall",
    },
    {
        title: "Lawn Care & Maintenance",
        desc: "Routine care to keep your lawn healthy, clean, and looking its best year-round.",
        image: PHOTOS.lawnPaverEdge,
        bullets: ["Weekly mowing, trimming & blowing", "Fertilization & weed control", "Spring & fall cleanups"],
        icon: "Leaf",
    },
    {
        title: "Seasonal Decorations",
        desc: "Holiday lighting, planters, and seasonal accents that make your home glow.",
        image: PHOTOS.circularStonePatio,
        bullets: ["Custom holiday lighting", "Seasonal planters", "Install + take-down service"],
        icon: "Sparkle",
    },
];

export const SERVICES_COMMERCIAL = [
    {
        title: "Full Property Landscaping",
        desc: "Cohesive, low-maintenance designs that elevate curb appeal and tenant experience.",
        image: PHOTOS.commercialAerialGreenSpace,
        bullets: ["Office parks & HOAs", "Streetscape planting", "Irrigation & drainage"],
        icon: "Buildings",
    },
    {
        title: "Maintenance Contracts",
        desc: "Predictable, contracted service plans with a single dedicated crew lead.",
        image: PHOTOS.commercialManicuredGarden,
        bullets: ["Monthly & seasonal scopes", "Detailed reporting", "Single point of contact"],
        icon: "ClipboardText",
    },
    {
        title: "Snow Removal",
        desc: "Reliable snow and ice clearing for driveways, walkways, and commercial lots — done before the morning starts.",
        image: PHOTOS.snowPlowTruck,
        bullets: ["24/7 storm response", "Plowing, shoveling, salting", "Per-event or seasonal contracts"],
        icon: "Snowflake",
    },
    {
        title: "Water Mitigation",
        desc: "Dry creek beds, French drains, and regrading that move water away from buildings and protect your landscape.",
        image: PHOTOS.drainageGrate,
        bullets: ["Dry creek beds & river rock swales", "French drains & downspout extensions", "Regrading & slope stabilisation"],
        icon: "Drop",
    },
    {
        title: "General Contracting",
        desc: "Trusted crews for residential and commercial site work, masonry, and outdoor build-outs.",
        image: PHOTOS.outdoorKitchen,
        bullets: ["Site preparation", "Concrete & masonry", "Coordinated subcontractors"],
        icon: "HardHat",
    },
];

export const PROCESS_STEPS = [
    { n: "01", title: "Consultation", desc: "We meet you on site, walk the property, and listen — to your routines, your goals, and the way you want to use the space." },
    { n: "02", title: "Design Planning", desc: "Concept sketches, plant palettes, and material samples come together into a clear, costed plan you can sign off on." },
    { n: "03", title: "Material Selection", desc: "Stone, pavers, plantings, and lighting are chosen with you — sourced from suppliers we've trusted for decades." },
    { n: "04", title: "Installation", desc: "Our own crews build it. No surprise subcontractors. Tidy job sites, clear daily updates, and craftsmanship at every step." },
    { n: "05", title: "Final Walkthrough", desc: "We walk every inch of the project together, hand off care guidance, and stand behind the work long after we leave." },
];

export const PORTFOLIO_ITEMS = [
    { id: 1, category: "Landscaping", title: "Lush Flowering Backyard Garden", image: PHOTOS.lushFloweringGarden, span: "lg:col-span-2 lg:row-span-2" },
    { id: 2, category: "Landscaping", title: "Pool, Patio & Outdoor Living", image: PHOTOS.poolPatioOutdoorLiving, span: "lg:col-span-2" },
    { id: 3, category: "Hardscaping", title: "Bluestone Patio with Up-lighting", image: PHOTOS.bluestonePatioDusk },
    { id: 4, category: "Landscaping", title: "Bluestone Walkway with Coneflower Bed", image: PHOTOS.walkwayWithConeflowers },
    { id: 5, category: "Hardscaping", title: "Curved Retaining Wall & Steps", image: PHOTOS.retainingWallCurved },
    { id: 6, category: "Hardscaping", title: "Outdoor Kitchen & Bluestone Patio", image: PHOTOS.outdoorKitchen, span: "lg:col-span-2" },
    { id: 7, category: "Landscaping", title: "Dry Creek Bed for Drainage", image: PHOTOS.dryCreekBed },
    { id: 8, category: "Hardscaping", title: "Cobblestone Driveway", image: PHOTOS.cobblestoneDriveway },
    { id: 9, category: "Landscaping", title: "Front Yard with Arborvitae Privacy Screen", image: PHOTOS.frontYardArborvitae },
    { id: 10, category: "Hardscaping", title: "Circular Bluestone Patio with Seat Wall", image: PHOTOS.circularStonePatio },
    { id: 11, category: "Landscaping", title: "Stone Wall, Hedge & Lawn", image: PHOTOS.stoneWallHedge },
    { id: 12, category: "Lawn Care", title: "Manicured Lawn Edge", image: PHOTOS.lawnPaverEdge },
    { id: 13, category: "Hardscaping", title: "Backyard Patio Project", image: PHOTOS.project8 },
];

export const PORTFOLIO_CATEGORIES = ["All", "Landscaping", "Hardscaping", "Lawn Care"];

export const TESTIMONIALS = [
    {
        quote: "They redesigned our entire backyard — patio, plantings, lighting. Three years on, it still looks like the day they finished.",
        name: "Sarah M.",
        role: "Homeowner, Arlington VA",
    },
    {
        quote: "Our HOA has used Great Outdoors for over a decade. Reliable crews, clean job sites, and they actually answer the phone.",
        name: "David L.",
        role: "HOA President, Falls Church VA",
    },
    {
        quote: "Snow response is the best on our street. They're done before I've finished my coffee.",
        name: "Karen P.",
        role: "Property Manager, McLean VA",
    },
    {
        quote: "Honest pricing, beautiful work. Our front yard finally looks like it belongs to the house.",
        name: "Mark & Jenna R.",
        role: "Homeowners, Vienna VA",
    },
];

export const STATS = [
    { value: "27+", label: "Years in Business" },
    { value: "1998", label: "Established" },
    { value: "1,200+", label: "Properties Served" },
    { value: "DMV", label: "Service Area" },
];

export const MAINTENANCE_CALENDAR = [
    {
        month: "March",
        title: "Pre-Emergent & Turf Renewal",
        items: [
            "Pre-emergent application with turf renovation",
            "12-week follow-up visit program initiated",
        ],
    },
    {
        month: "March – November",
        title: "Weekly Lawn Service",
        items: [
            "Mowing, trimming, and blowing of all hard surfaces",
            "Driveways, patios, and walkways cleared each visit",
            "Approximately 27 service visits across the season",
        ],
    },
    {
        month: "April",
        title: "Spring Clean Up",
        items: [
            "Debris removal — leaves, weeds, and sticks",
            "Weeding of all ornamental beds",
            "Cut down all perennials",
            "Shear and prune ornamentals & shrubs",
            "Trim ground cover and edge all beds",
            "Install fresh mulch to all beds",
            "Haul all excess debris off-site",
        ],
    },
    {
        month: "June",
        title: "Post-Emergent Application",
        items: ["Granular post-emergent application for turf"],
    },
    {
        month: "July",
        title: "Mid-Season Refresh",
        items: ["Weeding, pruning, and debris removal"],
    },
    {
        month: "October",
        title: "Fall Prep",
        items: [
            "Weeding, pruning, and debris removal",
            "Prep for fall and winter conditions",
            "Fall ornamental fertilisation",
        ],
    },
    {
        month: "October – December",
        title: "Leaf Removal",
        items: [
            "Leaf removal and haul to dump",
            "Approximately 3 visits per season",
            "$400 per visit",
        ],
    },
];

export const SERVICE_OPTIONS = [
    "Landscape Design & Installation",
    "Hardscaping Design & Installation",
    "Lawn Care & Maintenance",
    "Garden & Plant Installation",
    "Snow Removal",
    "Water Mitigation",
    "Seasonal Decorations",
    "General Contracting",
    "Annual Maintenance Program",
    "Other",
];
